import React, {
  useCallback,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as THREE from 'three';

import Cube360 from './cube360';
import Sphere360 from './sphere360';

import { PolygonDrawingOverlay } from './PolygonComponent';
import { DEFAULT_POLYGON_STYLE } from './PolygonComponent/constants';
import { updatePrevRotation } from 'store/actions';
import { calcCamRotationY } from 'containers/world/utils';
import { trackTimeOnScene } from 'gaTracking';
import { TRANSITION_METHOD } from './transition.const';

const CUBE_TOGGLE = {
  A: 'SCENE_A',
  B: 'SCENE_B',
};

window.isIntroAnimation = true;
window.transitionMethod = '';

const SceneController = (props) => {
  const { tour } = useSelector((state) => state);
  const { pano, scenes, onSwitchScene } = props;
  //eslint-disable-next-line
  const [hoveredMarkerIdFromMarkers, setHoveredMarkerIdFromMarkers] =
    useState(null);
  const lastScene = useRef(pano);
  const [sceneAData, setSceneAData] = useState({
    scene: pano,
    stepPosition: [0, 0, 0],
    sceneRotation: [0, 0, 0],
  });
  const [sceneBData, setSceneBData] = useState({
    scene: null,
    stepPosition: [0, 0, 0],
    sceneRotation: [0, 0, 0],
  });

  const [currentCube, setCurrentCube] = useState(CUBE_TOGGLE.A);
  const dispatch = useDispatch();
  const prevRotation = useRef(0);
  const rotations = useRef([{ rad: 0, deg: 0 }]);
  const stepClickingData = useRef(null);

  // Default polygons - can be imported or set as initial state
  const DEFAULT_POLYGONS = [
    // Polygon 1: Concave shape on left wall
    {
      id: 'polygon-1',
      points: [
        { x: -500, y: -126.53, z: 303.89 },
        { x: -500, y: -123.28, z: 292.09 },
        { x: -500, y: -118.58, z: 277.64 },
        { x: -500, y: -111.05, z: 258.41 },
        { x: -500, y: -100.72, z: 242.44 },
        { x: -500, y: -89.15, z: 233.94 },
        { x: -500, y: -83.24, z: 229.96 },
        { x: -500, y: -77.53, z: 241.54 },
        { x: -500, y: -74.28, z: 255.18 },
        { x: -500, y: -68.67, z: 257.48 },
        { x: -500, y: -56.97, z: 243.8 },
        { x: -500, y: -43.22, z: 210.86 },
        { x: -500, y: -59.02, z: 235.73 },
        { x: -500, y: -67.85, z: 242.45 },
        { x: -500, y: -72.24, z: 241.1 },
        { x: -500, y: -80.8, z: 223.37 },
        { x: -500, y: -84.75, z: 216.85 },
        { x: -500, y: -92.6, z: 220.75 },
        { x: -500, y: -103.19, z: 228.19 },
        { x: -500, y: -118.28, z: 256.57 },
        { x: -500, y: -122.37, z: 264.91 },
        { x: -500, y: -128.34, z: 262.59 },
      ],
    },
    // Polygon 2: Small shape
    {
      id: 'polygon-2',
      points: [
        { x: -500, y: -148.67, z: 381.48 },
        { x: -500, y: -139.87, z: 358.34 },
        { x: -500, y: -141.42, z: 353.4 },
        { x: -500, y: -146.46, z: 363.32 },
        { x: -500, y: -151.16, z: 373.45 },
      ],
    },
    // Polygon 3: Large triangular shape
    {
      id: 'polygon-3',
      points: [
        { x: -500, y: -156.26, z: 412.06 },
        { x: -488.49, y: -174.07, z: 500 },
        { x: -465.45, y: -238.11, z: 500 },
        { x: -405.07, y: -385.8, z: 500 },
        { x: -298.61, y: -500, z: 440.69 },
        { x: -352.53, y: -500, z: 376.89 },
        { x: -500, y: -345.37, z: 495.66 },
        { x: -500, y: -198.49, z: 480.36 },
        { x: -500, y: -176.79, z: 463.29 },
        { x: -500, y: -163.22, z: 421.85 },
      ],
    },
    {
      id: 'polygon-4',
      points: [
        { x: -298.64, y: -500, z: 441.27 },
        { x: -328.85, y: -500, z: 404.35 },
        { x: -352.44, y: -500, z: 377.43 },
        { x: -79.07, y: -500, z: 125.55 },
        { x: 145.24, y: -500, z: -76.87 },
        { x: 500, y: -417.09, z: -391.81 },
        { x: 500, y: -381.89, z: -317.22 },
        { x: 245.09, y: -500, z: -63.53 },
        { x: 89.9, y: -500, z: 72.62 },
        { x: -77.46, y: -500, z: 222.93 },
        { x: -225.71, y: -500, z: 360.07 },
        { x: -236.27, y: -500, z: 369.37 },
      ],
    },
    {
      id: 'polygon-5',
      points: [
        { x: 500, y: -417.05, z: -392.19 },
        { x: 500, y: -382.29, z: -316.85 },
        { x: 500, y: -322.83, z: -336.37 },
        { x: 500, y: -278.54, z: -341.45 },
        { x: 500, y: -195.35, z: -389.65 },
        { x: 500, y: -172.8, z: -398.58 },
        { x: 500, y: -160.53, z: -401.55 },
        { x: 500, y: -134.09, z: -378.42 },
        { x: 500, y: -113.13, z: -359.61 },
        { x: 500, y: -99.58, z: -356.66 },
        { x: 500, y: -81.86, z: -363.59 },
        { x: 500, y: -89.95, z: -380.61 },
        { x: 500, y: -112.38, z: -381.61 },
        { x: 500, y: -152.03, z: -417.49 },
        { x: 500, y: -167.33, z: -433.43 },
        { x: 500, y: -183.58, z: -438.98 },
        { x: 500, y: -278.15, z: -414.63 },
      ],
    },
  ];

  // Initialize polygons from DEFAULT_POLYGONS
  const getInitialPolygons = () => {
    return DEFAULT_POLYGONS.map((poly) => ({
      id: poly.id || crypto.randomUUID(),
      points: poly.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
      style: DEFAULT_POLYGON_STYLE,
    }));
  };

  // Polygon tool state
  // const [polygonToolActive, setPolygonToolActive] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [polygons, setPolygons] = useState(getInitialPolygons);
  const [draftPointsCount, setDraftPointsCount] = useState(0);
  const [polygonStyle, setPolygonStyle] = useState(DEFAULT_POLYGON_STYLE);

  // Expose polygon functions globally
  useEffect(() => {
    window._polygons = polygons;
    window._setPolygons = setPolygons;
    window._setPolygonDrawMode = setIsDrawingMode;
    window._draftPointsCount = draftPointsCount;
    window._polygonStyle = polygonStyle;
    window._setPolygonStyle = setPolygonStyle;

    window._clearAllPolygons = () => {
      setPolygons([]);
      setIsDrawingMode(false);
      setDraftPointsCount(0);
    };

    // Load default polygons from DEFAULT_POLYGONS constant
    window._loadDefaultPolygons = (defaultPolygons = DEFAULT_POLYGONS) => {
      if (defaultPolygons && defaultPolygons.length > 0) {
        const loadedPolygons = defaultPolygons.map((poly) => ({
          id: poly.id || crypto.randomUUID(),
          points: poly.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
          style: DEFAULT_POLYGON_STYLE,
        }));
        setPolygons(loadedPolygons);
      }
    };

    // Import polygons from JSON string or object
    window._importPolygons = (polygonData) => {
      try {
        let data = polygonData;
        if (typeof polygonData === 'string') {
          data = JSON.parse(polygonData);
        }
        if (!Array.isArray(data)) {
          data = [data];
        }
        const importedPolygons = data.map((poly) => ({
          id: poly.id || poly.polygonId || crypto.randomUUID(),
          points: poly.points.map((pt) => new THREE.Vector3(pt.x, pt.y, pt.z)),
          style: poly.style || DEFAULT_POLYGON_STYLE,
        }));
        setPolygons(importedPolygons);
      } catch (e) {
        console.error('Failed to import polygons:', e);
      }
    };

    window._exportPolygons = () => {
      const data = polygons.map((poly, idx) => ({
        polygonId: idx + 1,
        points: poly.points.map((pt) => ({
          x: Math.round(pt.x * 100) / 100,
          y: Math.round(pt.y * 100) / 100,
          z: Math.round(pt.z * 100) / 100,
        })),
      }));
      console.log('Polygon Data:', JSON.stringify(data, null, 2));
      // const jsonStr = JSON.stringify(data, null, 2);
      // const blob = new Blob([jsonStr], { type: 'application/json' });
      // const url = URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = `polygons-${Date.now()}.json`;
      // a.click();
      // URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line
  }, [polygons, draftPointsCount, polygonStyle]);

  const handleClickStep = useCallback(
    (sceneID, stepPosition, sceneRotation) => {
      const targetScene = scenes.find((scene) => scene._id === sceneID);
      if (targetScene) {
        stepClickingData.current = {
          scene: targetScene,
          stepPosition,
          sceneRotation,
        };
        onSwitchScene(targetScene.groupId, targetScene.id);
      }
    },
    [onSwitchScene, scenes]
  );

  useEffect(() => {
    // check if the next scene is a step => do ground nav too
    if (!stepClickingData.current && lastScene.current._id !== pano._id) {
      const step = (lastScene.current.steps || []).find(
        (s) => s.targetSceneId === pano._id
      );
      if (step) {
        const rotationY = calcCamRotationY(
          step.cameraPosition,
          step.targetSceneCameraPosition
        );
        stepClickingData.current = {
          scene: pano,
          stepPosition: step.stepPosition,
          sceneRotation: [0, rotationY, 0],
        };
      }
    }

    if (
      stepClickingData.current &&
      pano._id === stepClickingData.current.scene._id
    ) {
      window.transitionMethod = TRANSITION_METHOD.GROUND_NAV;
      const { scene, stepPosition, sceneRotation } = stepClickingData.current;
      prevRotation.current += sceneRotation[1];

      // Save prev rotation in store
      dispatch(updatePrevRotation(prevRotation.current));

      rotations.current.push({
        rad: prevRotation.current,
        deg: (prevRotation.current * 180) / Math.PI,
      });
      const targetSceneData = {
        scene,
        stepPosition: [...stepPosition],
        sceneRotation: [...sceneRotation],
      };
      if (currentCube === CUBE_TOGGLE.A) {
        setSceneBData(targetSceneData);
        setCurrentCube(CUBE_TOGGLE.B);
      } else {
        setSceneAData(targetSceneData);
        setCurrentCube(CUBE_TOGGLE.A);
      }
    }

    // handle change scene which is not step
    if (!stepClickingData.current && pano.panoType !== 'Pano') {
      window.transitionMethod = TRANSITION_METHOD.JUMP;
      prevRotation.current = 0;
      dispatch(updatePrevRotation(0));
      rotations.current.push({
        rad: 0,
        deg: 0,
      });
      const setSceneCallback = (d) => ({
        ...d,
        scene: pano,
      });
      if (currentCube === CUBE_TOGGLE.A) {
        setSceneAData(setSceneCallback);
      } else {
        setSceneBData(setSceneCallback);
      }
    }

    stepClickingData.current = null;

    lastScene.current = pano; // store current pano for reference next time pano change

    // eslint-disable-next-line
  }, [lastScene, scenes, pano]);

  const [cubeA, cubeB] = useMemo(() => {
    const commonCubeHandlers = {
      prevRotation: prevRotation.current,
      rotations: rotations.current,
      onClickStep: handleClickStep,
    };

    const firstCubeHandlers = {
      ...commonCubeHandlers,
      sceneData: sceneAData.scene,
      stepPosition: sceneAData.stepPosition,
      sceneRotation: sceneAData.sceneRotation,
      positionNext: sceneBData.stepPosition,
      active: currentCube === CUBE_TOGGLE.A,
    };

    const secondCubeHandlers = {
      ...commonCubeHandlers,
      sceneData: sceneBData.scene,
      stepPosition: sceneBData.stepPosition,
      sceneRotation: sceneBData.sceneRotation,
      positionNext: sceneAData.stepPosition,
      active: currentCube === CUBE_TOGGLE.B,
    };

    return [firstCubeHandlers, secondCubeHandlers];
  }, [
    prevRotation,
    rotations,
    handleClickStep,
    sceneAData,
    sceneBData,
    currentCube,
  ]);

  useEffect(() => {
    const intervalId = trackTimeOnScene({
      tour_id: tour.id,
      scene_id: props?.pano?._id,
    });
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line
  }, [props?.pano?.id]);

  // const handleClickShape = useCallback((shape) => {
  //   // Add your shape click logic here
  //   // Example: const targetSceneId = SHAPE_TO_SCENE[shape];
  //   // if (targetSceneId) { ... }
  // }, []);

  const handlePolygonComplete = useCallback((polygon) => {
    console.log('Polygon completed:', polygon);
  }, []);

  // const handleDrawModeToggle = useCallback(() => {
  //   setIsDrawingMode((prev) => !prev);
  // }, []);

  return (
    <>
      {/* <Suspense fallback={null}>
        {
          <SceneMarkers
            sceneId={pano?._id}
            onShapeClick={handleClickShape}
            onHoveredMarkerIdChange={setHoveredMarkerIdFromMarkers}
          />
        }
      </Suspense> */}
      <Cube360 {...props} pano={cubeA.sceneData} stepHandlers={cubeA} />
      <Cube360 {...props} pano={cubeB.sceneData} stepHandlers={cubeB} />
      <Sphere360 {...props} sceneData={pano} />

      {/* Polygon drawing overlay - renders inside 3D scene */}
      {pano?._id === '68d12cca208f82c724e17eb7' && (
        <PolygonDrawingOverlay
          sceneData={pano}
          isDrawingMode={isDrawingMode}
          onPolygonComplete={handlePolygonComplete}
          onDrawModeChange={setIsDrawingMode}
          onDraftPointsChange={setDraftPointsCount}
          polygons={polygons}
          setPolygons={setPolygons}
          style={polygonStyle}
        />
      )}

      {/* Expose polygon tool controls globally for the floating button */}
      {/* <PolygonToolBridge
        sceneData={pano}
        polygonToolActive={polygonToolActive}
        setPolygonToolActive={setPolygonToolActive}
        isDrawingMode={isDrawingMode}
        onDrawModeToggle={handleDrawModeToggle}
        polygonCount={polygons.length}
        polygonStyle={polygonStyle}
        setPolygonStyle={setPolygonStyle}
      /> */}

      {/* X: red. Y: green. Z: blue. */}
      {/* {configs.local && <axesHelper arg={[100]} />} */}
    </>
  );
};

// Separate component to expose controls via window
// const PolygonToolBridge = ({
//   sceneData,
//   polygonToolActive,
//   setPolygonToolActive,
//   isDrawingMode,
//   onDrawModeToggle,
//   polygonCount,
//   polygonStyle,
//   setPolygonStyle,
// }) => {
//   useEffect(() => {
//     window._polygonToolActive = polygonToolActive;
//     window._setPolygonToolActive = setPolygonToolActive;
//     window._polygonDrawMode = isDrawingMode;
//     window._setPolygonDrawMode = onDrawModeToggle;
//     window._polygonCount = polygonCount;
//     window._polygonStyle = polygonStyle;
//     window._setPolygonStyle = setPolygonStyle;
//   }, [
//     polygonToolActive,
//     setPolygonToolActive,
//     isDrawingMode,
//     onDrawModeToggle,
//     polygonCount,
//     polygonStyle,
//     setPolygonStyle,
//   ]);

//   return null;
// };

export default SceneController;
